import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios, { CancelTokenSource } from "axios";
import { validate as validateUuid } from "uuid";
import { ChatUatApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import { ChatUatResponseGroundedInEnum } from "../lusca-search-api/generated-src";

export interface ChatUatEntry {
  id: string;
  role: "user" | "assistant";
  content: string;
  groundedIn?: string[];
  isError?: boolean;
}

export interface ChatUatArtefact {
  raw: string;
  primary: string;
  details: string;
}

interface ChatUatTurn {
  prompt: string;
  conversationId?: string;
  generation: number;
  cancelSource: CancelTokenSource;
}

interface ValidChatUatResponse {
  conversationId: string;
  conversational: string;
  artefact: string;
  groundedIn: string[];
}

const REQUEST_RESET_ERROR =
  "This request could not be confirmed, so the conversation was reset. You can try again.";
const COVERAGE_LINE = /^(Evidence present:|Grounded in:)/;
const GROUNDING_SOURCES = new Set<string>(
  Object.values(ChatUatResponseGroundedInEnum)
);

let entryCounter = 0;
function nextEntryId(): string {
  entryCounter += 1;
  return `chat-uat-entry-${entryCounter}`;
}

function isGroundingSources(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (source) =>
        typeof source === "string" &&
        source.trim().length > 0 &&
        GROUNDING_SOURCES.has(source)
    ) &&
    new Set(value).size === value.length
  );
}

function validateResponse(value: unknown): ValidChatUatResponse {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Invalid ChatUAT response");
  }

  const { conversationId, conversational, artefact, groundedIn } = value as {
    conversationId?: unknown;
    conversational?: unknown;
    artefact?: unknown;
    groundedIn?: unknown;
  };
  if (
    typeof conversationId !== "string" ||
    conversationId !== conversationId.toLowerCase() ||
    !validateUuid(conversationId) ||
    typeof conversational !== "string" ||
    !conversational.trim() ||
    typeof artefact !== "string" ||
    !artefact.trim() ||
    !isGroundingSources(groundedIn)
  ) {
    throw new Error("Invalid ChatUAT response");
  }

  return { conversationId, conversational, artefact, groundedIn };
}

export function parseArtefact(raw: string): ChatUatArtefact {
  const body = raw.trim();
  const primary: string[] = [];
  const details: string[] = [];
  body.split("\n").forEach((line) => {
    (COVERAGE_LINE.test(line.trim()) ? details : primary).push(line);
  });
  return {
    raw: body,
    primary: primary
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim(),
    details: details.join("\n").trim(),
  };
}

export default function useChatUat() {
  const [entries, setEntries] = useState<ChatUatEntry[]>([]);
  const [rawArtefact, setRawArtefact] = useState<string | null>(null);
  const conversationId = useRef<string | undefined>(undefined);
  const activeRequest = useRef<CancelTokenSource | null>(null);
  const generation = useRef(0);
  const submitting = useRef(false);

  useEffect(
    () => () => {
      generation.current += 1;
      submitting.current = false;
      activeRequest.current?.cancel("ChatUAT request abandoned");
      activeRequest.current = null;
    },
    []
  );

  const {
    isLoading,
    mutate,
    reset: resetMutation,
  } = useMutation<ValidChatUatResponse, Error, ChatUatTurn>({
    mutationFn: async ({
      prompt,
      conversationId: turnConversationId,
      cancelSource,
    }: ChatUatTurn): Promise<ValidChatUatResponse> => {
      const api = ChatUatApiFactory();
      const { data } = await api.chatUat(
        {
          prompt,
          conversationId: turnConversationId,
        },
        {
          cancelToken: cancelSource.token,
        }
      );
      return validateResponse(data);
    },
    onSuccess: (data, turn) => {
      if (turn.generation !== generation.current) {
        return;
      }
      conversationId.current = data.conversationId;
      setRawArtefact(data.artefact?.trim() || null);
      setEntries((prev) => [
        ...prev,
        {
          id: nextEntryId(),
          role: "assistant",
          content: data.conversational.trim(),
          groundedIn: data.groundedIn ? Array.from(data.groundedIn) : undefined,
        },
      ]);
    },
    onError: (_error, turn) => {
      if (turn.generation !== generation.current) {
        return;
      }
      conversationId.current = undefined;
      setRawArtefact(null);
      setEntries([
        {
          id: nextEntryId(),
          role: "assistant",
          content: REQUEST_RESET_ERROR,
          isError: true,
        },
      ]);
    },
    onSettled: (_data, _error, turn) => {
      if (activeRequest.current === turn.cancelSource) {
        activeRequest.current = null;
      }
      if (turn.generation === generation.current) {
        submitting.current = false;
      }
    },
  });

  const sendMessage = useCallback(
    (text: string) => {
      const prompt = text.trim();
      if (!prompt || isLoading || submitting.current) {
        return;
      }
      const cancelSource = axios.CancelToken.source();
      submitting.current = true;
      activeRequest.current = cancelSource;
      setRawArtefact(null);
      setEntries((prev) => [
        ...prev,
        { id: nextEntryId(), role: "user", content: prompt },
      ]);
      mutate({
        prompt,
        conversationId: conversationId.current,
        generation: ++generation.current,
        cancelSource,
      });
    },
    [isLoading, mutate]
  );

  const reset = useCallback(() => {
    generation.current += 1;
    submitting.current = false;
    conversationId.current = undefined;
    activeRequest.current?.cancel("ChatUAT conversation reset");
    activeRequest.current = null;
    resetMutation();
    setEntries([]);
    setRawArtefact(null);
  }, [resetMutation]);

  const artefact = useMemo(
    () => (rawArtefact ? parseArtefact(rawArtefact) : null),
    [rawArtefact]
  );

  return {
    entries,
    artefact,
    sendMessage,
    reset,
    isLoading,
  };
}
